import React, { createContext, useContext, useMemo } from 'react';
import type {
  ExperienceConfig,
  ApiConfig,
  TrackingConfig,
  SiteConfig,
  LocationConfig,
} from '../types/config';
import { getDefaultConfig } from '../config/environments';
import { configureApiClient } from '../services/api/utils';

/**
 * Context for experience configuration
 */
const ExperienceConfigContext = createContext<ExperienceConfig | null>(null);

export interface ExperienceConfigProviderProps {
  children: React.ReactNode;
  config: ExperienceConfig;
}

/**
 * Provider for experience configuration
 * This allows injecting API endpoints, tracking config, and site config
 */
export const ExperienceConfigProvider: React.FC<
  ExperienceConfigProviderProps
> = ({ children, config }) => {
  // Configure the API client when config changes
  useMemo(() => {
    if (config.api) {
      configureApiClient(config.api);
    }
  }, [config.api]);

  const value = useMemo(() => config, [config]);

  return (
    <ExperienceConfigContext.Provider value={value}>
      {children}
    </ExperienceConfigContext.Provider>
  );
};

/**
 * Hook to access the experience configuration
 */
export function useExperienceConfig(): ExperienceConfig {
  const context = useContext(ExperienceConfigContext);
  if (!context) {
    console.warn(
      'useExperienceConfig must be used within an ExperienceConfigProvider',
    );
    return getDefaultConfig();
  }
  return context;
}

/**
 * Hook to access just the API configuration
 */
export function useApiConfig(): ApiConfig {
  const config = useExperienceConfig();
  return config.api;
}

/**
 * Hook to access tracking configuration
 */
export function useTrackingConfig(): TrackingConfig | undefined {
  const config = useExperienceConfig();
  return config.tracking;
}

/**
 * Hook to access site configuration
 */
export function useSiteConfig(): SiteConfig | undefined {
  const config = useExperienceConfig();
  return config.site;
}

/**
 * Hook to access location configuration
 */
export function useLocationConfig(): LocationConfig | undefined {
  const config = useExperienceConfig();
  return config.location;
}

export default ExperienceConfigProvider;
